import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { useInViewOnce } from "@/hooks/useInViewOnce";
import GradientText from "@/components/home/primitives/GradientText";
import SectionHeading from "@/components/home/primitives/SectionHeading";
import StatusBadge from "@/components/home/primitives/StatusBadge";
import { CHANNELS } from "@/data/marketing/homepage";
import type { Channel } from "@/types/homepage";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const ChannelCard = ({ channel, index }: { channel: Channel; index: number }) => {
  const { ref, inView } = useInViewOnce<HTMLDivElement>();

  const body = (
    <>
      <div className="flex items-start justify-between">
        <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-genie-primary/10">
          <GenieIcon name={channel.genieIcon} size={28} className="h-7 w-7" />
        </div>
        <StatusBadge status={channel.status} />
      </div>
      <h3 className="mt-4 text-lg font-semibold text-[#222B45]">{channel.name}</h3>
      <p className="mt-2 text-sm leading-relaxed text-gray-600">{channel.tagline}</p>
      <p className="mt-3 text-xs leading-relaxed text-gray-500">
        <span className="font-semibold text-gray-600">Replaces</span>{" "}
        {channel.replaces}
      </p>
      {channel.href && (
        <span className="mt-4 inline-flex items-center text-sm font-medium text-genie-primary">
          See it
          <ChevronRight className="ml-0.5 h-4 w-4" />
        </span>
      )}
    </>
  );

  const cardClass =
    "group block h-full rounded-2xl border border-[#E5EAF0] bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:border-genie-primary/40";

  return (
    <div
      ref={ref}
      className={cn(
        "h-full transition-all duration-500 ease-out",
        inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
      )}
      style={{ transitionDelay: `${index * 70}ms` }}
    >
      {channel.href ? (
        <Link to={channel.href} className={cardClass}>
          {body}
        </Link>
      ) : (
        <div className={cardClass}>{body}</div>
      )}
    </div>
  );
};

export default function HomeChannels() {
  return (
    <section className="w-full bg-[#F9FAFC] py-16 sm:py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          eyebrow="Same genie, every doorway"
          title={
            <>
              One genie. <GradientText>Every channel.</GradientText>
            </>
          }
          subtitle="Voice, chat, phone numbers, QR codes, website, mobile, email, outbound. The same genie on every channel. Same brain, same voice, same answers, whichever doorway your customer walks through."
        />

        <div className="mt-12 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {CHANNELS.map((channel, i) => (
            <ChannelCard key={channel.id} channel={channel} index={i} />
          ))}

          {/* Continuity / CTA cell */}
          <div className="flex h-full flex-col justify-between rounded-2xl bg-gradient-to-br from-[#4E9CFF] to-[#FF4DB8] p-6 text-white shadow-lg">
            <p className="text-base font-semibold leading-snug">
              Same conversation, every doorway.
            </p>
            <p className="mt-2 text-sm text-white/85">
              Call, chat, scan or email: one history follows the customer
              everywhere.
            </p>
            <Link
              to="/talk-to-genie"
              className="mt-5 inline-flex items-center justify-center rounded-xl bg-white px-4 py-2.5 text-sm font-semibold text-genie-primary transition hover:bg-white/90"
            >
              Talk to a genie
              <ChevronRight className="ml-1 h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
